<template>
  <div class="car-marketplace">
    <!-- 顶部导航栏 -->
    <header class="header">
      <div class="nav-container">
        <div class="logo">
          <span class="logo-text">AUTOMART</span>
        </div>
        <nav class="nav-menu">
          <a href="#home" class="nav-link">{{ getTextLocalized('nav.home') }}</a>
          <a href="#cars" class="nav-link">{{ getTextLocalized('nav.cars') }}</a>
          <a href="#categories" class="nav-link">{{ getTextLocalized('nav.categories') }}</a>
          <a href="#about" class="nav-link">{{ getTextLocalized('nav.about') }}</a>
        </nav>
        <div class="search-box">
          <input type="text" :placeholder="getTextLocalized('search.placeholder')" class="search-input" v-model="searchQuery" @keyup.enter="handleSearch">
          <button class="search-btn" @click="handleSearch">
            <i class="el-icon-search"></i>
          </button>
        </div>
        <div class="city-selector">
          <select v-model="selectedCity" class="city-select" @change="handleCityChange">
            <option v-for="city in localizedCities" :key="city.code" :value="city.code">
              {{ city.name }}
            </option>
          </select>
        </div>
          <div class="language-switcher">
            <button
              v-for="lang in languages"
              :key="lang.code"
              :class="['lang-btn', { active: currentLang === lang.code }]"
              @click="switchLanguage(lang.code)"
            >
              {{ lang.name }}
            </button>
          </div>
          </div>
    </header>

    <!-- 筛选和展示区域 -->
    <section class="main-section" id="home">
      <div class="main-container">
        <div class="main-layout">
          <!-- 左侧品牌区域 -->
          <div class="brand-sidebar">
            <div class="filter-section">
              <h3 class="filter-title">热门品牌</h3>
              <div class="brand-grid">
                <div
                  v-for="brand in mockData.brands.slice(0, 12)"
                  :key="brand.id"
                  :class="['brand-item', { active: selectedBrand === brand.id }]"
                  @click="filterByBrand(brand.id)"
                >
                  <img :src="brand.logo" :alt="brand.name" class="brand-logo">
                  <span class="brand-name">{{ brand.translations[currentLang].name }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 中间轮播图区域 -->
          <div class="carousel-section">
            <div class="carousel-container">
              <div
                v-for="(slide, index) in localizedCarouselImages"
                :key="slide.id"
                :class="['carousel-slide', { active: currentSlide === index }]"
              >
                <img :src="slide.image" :alt="slide.title" class="carousel-image">
                <div class="carousel-content">
                  <h2 class="carousel-title">{{ slide.title }}</h2>
                  <p class="carousel-subtitle">{{ slide.subtitle }}</p>
                  <button class="carousel-btn" @click="scrollToCars">{{ slide.buttonText }}</button>
                </div>
              </div>
              <div class="carousel-indicators">
                <span
                  v-for="(slide, index) in localizedCarouselImages"
                  :key="slide.id"
                  :class="['indicator', { active: currentSlide === index }]"
                  @click="currentSlide = index"
                ></span>
              </div>
              <button class="carousel-arrow carousel-arrow-prev" @click="prevSlide">
                <i class="el-icon-arrow-left"></i>
              </button>
              <button class="carousel-arrow carousel-arrow-next" @click="nextSlide">
                <i class="el-icon-arrow-right"></i>
              </button>
            </div>
          </div>

          <!-- 右侧车辆类型区域 -->
          <div class="type-sidebar">
            <div class="filter-section">
              <h3 class="filter-title">车辆类型</h3>
              <div class="type-grid">
                <div
                  v-for="type in localizedVehicleTypes"
                  :key="type.id"
                  :class="['type-item', { active: selectedType === type.code }]"
                  @click="filterByType(type.code)"
                >
                  <i :class="type.icon" class="type-icon"></i>
                  <span class="type-name">{{ type.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 车辆展示区域 -->
    <section class="cars-section" id="cars">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">精选车辆</h2>
          <p class="section-subtitle">为您推荐优质二手车</p>
        </div>

        <div class="cars-container">
          <div v-if="filteredCars.length === 0" class="no-cars">
            暂无车辆数据
          </div>
          <div class="car-card" v-for="car in filteredCars" :key="car.id" @click="goToCarDetail(car.id)">
            <div class="car-image-container">
              <img :src="car.image" :alt="car.name || '车辆'" class="car-image" />
              <div class="car-overlay">
                <button class="favorite-btn" @click.stop="toggleFavorite(car.id)">
                  <i :class="car.isFavorite ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                </button>
              </div>
              <div class="car-badge" v-if="car.badge">{{ car.badge }}</div>
            </div>
            <div class="car-content">
              <div class="car-brand">
                <img v-if="car.brandLogo" :src="car.brandLogo" :alt="car.brandName" class="brand-logo-small">
                <span class="brand-name">{{ car.brandName }}</span>
              </div>
              <div class="car-header">
                <h3 class="car-title">{{ car.name }}</h3>
                <div class="car-price">
                  <span class="price">{{ car.price }}</span>
                  <span class="price-unit">万</span>
                </div>
              </div>
              <div class="car-details">
                <div class="detail-row">
                  <span class="detail-item">{{ car.year }}年</span>
                  <span class="detail-divider">·</span>
                  <span class="detail-item">{{ car.mileage }}万公里</span>
                  <span class="detail-divider">·</span>
                  <span class="detail-item">{{ car.distance }}</span>
                </div>
                <div class="detail-row condition-row">
                  <span class="detail-item condition-badge">{{ car.condition }}</span>
                  <span class="detail-divider">·</span>
                  <span class="detail-item">{{ car.location }}</span>
                </div>
              </div>
              <div class="car-features">
                <span class="feature-tag" v-for="feature in car.features.slice(0, 3)" :key="feature">{{ feature }}</span>
              </div>
              <div class="car-action">
                <button class="action-btn primary" @click.stop="goToCarDetail(car.id)">
                  查看详情
                  <i class="el-icon-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 服务保障区域 -->
    <section class="services-section">
      <div class="container">
        <div class="services-grid">
          <div class="service-item" v-for="service in localizedServices" :key="service.id">
            <div class="service-icon">
              <i :class="service.icon"></i>
            </div>
            <div class="service-info">
              <h4 class="service-title">{{ service.title }}</h4>
              <p class="service-desc">{{ service.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>


    <!-- 底部 -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-section">
            <h3>二手车专卖</h3>
            <p>专业的二手车交易平台，为您提供最优质的服务</p>
            <div class="social-links">
              <a href="#" class="social-link"><i class="el-icon-s-custom"></i></a>
              <a href="#" class="social-link"><i class="el-icon-chat-dot-round"></i></a>
              <a href="#" class="social-link"><i class="el-icon-phone"></i></a>
            </div>
          </div>
          <div class="footer-section">
            <h4>服务项目</h4>
            <ul>
              <li><a href="#">车辆检测</a></li>
              <li><a href="#">价格评估</a></li>
              <li><a href="#">过户手续</a></li>
              <li><a href="#">售后保障</a></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>{{ getTextLocalized('footer.aboutUs') }}</h4>
            <ul>
              <li><a href="#">{{ getTextLocalized('footer.companyIntro') }}</a></li>
              <li><a href="#">{{ getTextLocalized('footer.contactUs') }}</a></li>
              <li><a href="#">{{ getTextLocalized('footer.privacy') }}</a></li>
              <li><a href="#">{{ getTextLocalized('footer.terms') }}</a></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>{{ getTextLocalized('footer.contactInfo') }}</h4>
            <div class="contact-info">
              <p><i class="el-icon-phone"></i> {{ getTextLocalized('footer.phone') }}</p>
              <p><i class="el-icon-message"></i> {{ getTextLocalized('footer.email') }}</p>
              <p><i class="el-icon-location"></i> {{ getTextLocalized('footer.address') }}</p>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; 2024 二手车专卖. All Rights Reserved. <a href="#">京ICP备xxxxxxxx号</a></p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import mockData, { getText, getLocalizedData } from '@/mockData.js'

// 导入样式文件
import '@/styles/index.js'

export default {
  name: 'Demo',
  data() {
    return {
      mockData,
      searchQuery: '',
      activeCategory: null,
      selectedCity: localStorage.getItem('selectedCity') || 'beijing',
      selectedBrand: null,
      selectedType: null,
      currentSlide: 0,
      carouselTimer: null,
      favorites: [],
      currentLang: localStorage.getItem('language') || 'zh',
      languages: [
        { code: 'zh', name: '中文' },
        { code: 'en', name: 'EN' },
        { code: 'ru', name: 'РУ' }
      ],
            tradeGuarantee: {
              title: '交易保障',
              desc: '第三方托管交易，确保买卖双方权益'
            },
            transferService: {
              title: '过户服务',
              desc: '一站式过户服务，省心快捷'
            },
            afterSales: {
              title: '售后保障',
              desc: '365天售后保障，随时为您服务'
            }
    }
  },
  computed: {
    featuredCars() {
      // 简化处理，直接返回车辆数据
      return this.mockData.vehicles.map(car => {
        const brand = this.mockData.brands.find(b => b.id === car.brandId);
        return {
          ...car,
          brandLogo: brand?.logo,
          brandName: brand?.name,
          // 如果有translations，使用当前语言的名称
          name: car.translations && car.translations[this.currentLang] ?
            car.translations[this.currentLang].name : car.brand + ' ' + car.year
        };
      });
    },
    getTextLocalized() {
      return (path) => getText(path, this.currentLang);
    },
    filteredCars() {
      // 直接返回所有车辆，不进行筛选和排序
      return this.featuredCars;
    },
    localizedBrands() {
      return getLocalizedData(this.mockData.brands, this.currentLang);
    },
    localizedVehicleTypes() {
      return getLocalizedData(this.mockData.vehicleTypes, this.currentLang);
    },
    localizedPriceRanges() {
      return getLocalizedData(this.mockData.priceRanges, this.currentLang);
    },
    localizedCarouselImages() {
      return getLocalizedData(this.mockData.carouselImages, this.currentLang);
    },
    localizedCities() {
      return getLocalizedData(this.mockData.cities, this.currentLang);
    },
    localizedServices() {
      return getLocalizedData(this.mockData.services, this.currentLang);
    },
    hasMoreCars() {
      return this.filteredCars.length > 8;
    }
  },
  methods: {
    getText(path) {
      try {
        // 确保currentLang有默认值
        const lang = this.currentLang || 'zh';
        const keys = path.split('.');
        let value = this.translations[lang];

        if (!value) {
          console.warn(`No translations found for language: ${lang}, path: ${path}`);
          // 返回一个默认值而不是path
          if (path === 'car.year') return '年';
          if (path === 'car.kilometers') return '万公里';
          if (path === 'categories.unit') return '辆';
          return path;
        }

        for (const key of keys) {
          value = value && value[key];
        }

        if (value === undefined || value === null) {
          console.warn(`Translation key "${path}" not found for language ${lang}`);
          // 返回合理的默认值
          if (path === 'car.year') return '年';
          if (path === 'car.kilometers') return '万公里';
          if (path === 'categories.unit') return '辆';
          return path;
        }

        return value;
      } catch (error) {
        console.warn(`Error getting translation for "${path}":`, error);
        // 返回合理的默认值
        if (path === 'car.year') return '年';
        if (path === 'car.kilometers') return '万公里';
        if (path === 'categories.unit') return '辆';
        return path;
      }
    },
    switchLanguage(lang) {
      this.currentLang = lang;
      localStorage.setItem('language', lang);
      this.$forceUpdate();
    },
    handleCityChange() {
      localStorage.setItem('selectedCity', this.selectedCity);
      this.$forceUpdate();
    },
    handleSearch() {
      // 搜索逻辑已在computed中实现
      this.$forceUpdate();
    },
    filterByBrand(brandId) {
      this.selectedBrand = this.selectedBrand === brandId ? null : brandId;
    },
    filterByType(typeCode) {
      this.selectedType = this.selectedType === typeCode ? null : typeCode;
    },
    toggleFavorite(carId) {
      const car = this.featuredCars.find(c => c.id === carId);
      if (car) {
        car.isFavorite = !car.isFavorite;
        if (car.isFavorite) {
          this.favorites.push(carId);
        } else {
          this.favorites = this.favorites.filter(id => id !== carId);
        }
      }
    },
    goToCarDetail(carId) {
      this.$router.push({ name: 'CarDetail', params: { id: carId } });
    },
    contactSeller(carId) {
      // 这里可以实现联系卖家的逻辑
      this.$message.success('已发送咨询请求，卖家将尽快联系您！');
    },
    scrollToCars() {
      const element = document.getElementById('cars');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    showAssessmentDialog() {
      this.$message.info('免费评估功能即将上线，敬请期待！');
    },
    loadMoreCars() {
      // 这里可以实现加载更多车辆的逻辑
      this.$message.info('更多车辆加载中...');
    },
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.mockData.carouselImages.length;
    },
    prevSlide() {
      this.currentSlide = this.currentSlide === 0 ? this.mockData.carouselImages.length - 1 : this.currentSlide - 1;
    },
    startCarousel() {
      this.carouselTimer = setInterval(() => {
        this.nextSlide();
      }, 5000); // 每5秒切换一次
    }
  },
  mounted() {
    // 初始化收藏状态
    this.favorites.forEach(carId => {
      const car = this.featuredCars.find(c => c.id === carId);
      if (car) {
        car.isFavorite = true;
      }
    });

    // 启动轮播图自动播放
    this.startCarousel();
  },
  beforeDestroy() {
    // 清除定时器
    if (this.carouselTimer) {
      clearInterval(this.carouselTimer);
    }
  }
}
</script>

